import React, { FC } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { Switch } from '@mui/material';
import Refresh from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';
import {
  Header,
  HeaderTitle,
  HeaderTitleContainer,
  HeaderButtonsContainer,
  SwitchContainer,
  RefreshButton,
} from './styles';
import VaultButton from '../../Shared/VaultButton';
import {
  updateLiveAssetModalProperties, openAssetModal, closeNewAssetModal,
} from '../../../state/liveEvents/liveAssetModal';
import { searchLiveAssets } from '../../../state/liveEvents/liveEventsList';
import { IAppState } from '../../../state/reducers';
import { ILiveAssetFilters, updateLiveFilterValues } from '../../../state/filterAssets';
import { updateSearchQueryParams } from '../../../state/search';
import { refreshAssets as refreshAssetsApi } from '../../../api/vault/assets';
import { IAsset, IDataHit, IGameSearchResponse } from '../../../api/vault/searchEngine';

interface IOwnProps {
  refreshAssets: () => void;
}

interface IStateProps {
  filters: ILiveAssetFilters;
  searchResults: IGameSearchResponse;
}

interface IDispatchProps {
  updateLiveAssetModalProperties: (updates: any[]) => void;
  openAssetModal: (isEdit: boolean, isBulkEdit: boolean) => void;
  closeNewAssetModal: () => void;
  refreshAssets: () => void;
  updateLiveFilterValues: (liveAssetFilters: object) => void;
  updateSearchQueryParams: (params: { sortField?: string, sortOrder?: string, size?: number, page?: number }) => void;
}

interface IProps extends IOwnProps, IStateProps, IDispatchProps { }

const LiveEventsHeader: FC<IProps> = (props: IProps) => {
  const {
    updateLiveAssetModalProperties,
    openAssetModal,
    refreshAssets,
    filters,
    searchResults,
  } = props;

  const DEFAULT_BITRATE = 4000;

  let liveEvents: IDataHit<IAsset>[] = [];
  if (searchResults) {
    liveEvents = searchResults.hits.hits;
  }

  const handleCreateLiveEvent = () => {
    updateLiveAssetModalProperties([
      { name: 'start', value: moment().add(16, 'm') },
      { name: 'stop', value: moment().add(196, 'm') },
      { name: 'bitrate', value: DEFAULT_BITRATE },
    ]);

    openAssetModal(false, false);
  };

  const handleFilterChange = (value: string | Date | Array<string> | boolean, columnName: string) => {
    const {
      filters,
      updateLiveFilterValues,
      updateSearchQueryParams,
      updateLiveAssetModalProperties,
    } = props;

    updateSearchQueryParams({ page: 1 });

    updateLiveFilterValues({
      ...filters,
      [columnName]: value,
    });

    if (columnName === 'exchangeLiveEvent') {
      updateLiveAssetModalProperties([{ name: 'isExchangeLiveEvent', value }]);
    }

    setTimeout(() => { refreshAssets(); }, 100);
  };

  return (
    <Header>
      <HeaderTitleContainer data-auto="LiveEventsHeader">
        <HeaderTitle>Live Events</HeaderTitle>
      </HeaderTitleContainer>
      <HeaderButtonsContainer>
        <SwitchContainer>
          <span>Vault</span>
          <Switch checked={filters.exchangeLiveEvent} onChange={(e) => handleFilterChange(e.target.checked, 'exchangeLiveEvent')} />
          <span>Exchange</span>
        </SwitchContainer>
        <RefreshButton
          id="LiveEvents.RefreshLiveEvents"
          variant="primaryOnWhite"
          onClick={async () => {
            refreshAssetsApi(liveEvents.map((event) => event._id));
            toast.info('Refreshing live events...', { autoClose: 2000 });
            await refreshAssets();
            setTimeout(() => { refreshAssets(); }, 1000);
          }}
          data-auto="RefreshLiveEventBtn"
        >
          <Refresh sx={{ fontSize: 24 }} />
        </RefreshButton>
        <VaultButton
          id="LiveEvents.CreateLiveEvent"
          variant="primaryOnWhite"
          onClick={() => { handleCreateLiveEvent(); }}
          data-auto="CreateLiveEventBtn"
        >
          Add Live Event
        </VaultButton>
      </HeaderButtonsContainer>
    </Header>
  );
};

const mapStateToProps = (state: IAppState): IStateProps => ({
  filters: state.filterAssets.liveAssetFilters,
  searchResults: state.liveEventsList.searchResults,
});

const mapDispatchToProps: IDispatchProps = {
  updateLiveAssetModalProperties,
  openAssetModal,
  closeNewAssetModal,
  refreshAssets: searchLiveAssets,
  updateLiveFilterValues,
  updateSearchQueryParams,
};

export default connect(mapStateToProps, mapDispatchToProps)(LiveEventsHeader);
